import { keyLabel } from "./transpose";
import type { Score } from "./types";

/**
 * Cover art for a piece that has no illustration.
 *
 * 19 of the 30 pieces carry a picture from the printed book; the other 11 were
 * rendering nothing at all, so the page jumped straight from the nav to the
 * metadata and looked half-built next to its neighbours. Rather than invent a
 * photograph for a piece that doesn't have one, this draws the piece itself:
 * staff lines, its key and time signature, its title. Deterministic, quiet,
 * and in the same engraving language as the score below it — so an
 * un-illustrated piece reads as designed rather than as missing.
 */

/** Stable hue per piece so a title always gets the same cover. */
function hueOf(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return h;
}

export function PieceCover({ score }: { score: Score }) {
  const hue = hueOf(score.slug || score.title);
  const ink = `hsl(${hue} 38% 26%)`;
  const wash = `hsl(${hue} 42% 92%)`;
  const rule = `hsl(${hue} 26% 62%)`;
  const key = keyLabel(score.key);

  return (
    <figure className="overflow-hidden rounded-2xl border border-zinc-700">
      <svg
        viewBox="0 0 1200 260"
        className="h-auto w-full"
        role="img"
        aria-label={`${score.title} — ${key}, ${score.time[0]}/${score.time[1]}`}
      >
        <rect width="1200" height="260" fill={wash} />

        {/* a five-line stave, drawn faintly across the whole cover */}
        <g stroke={rule} strokeWidth="1.4" opacity="0.55">
          {[92, 112, 132, 152, 172].map((y) => (
            <line key={y} x1="0" x2="1200" y1={y} y2={y} />
          ))}
        </g>

        {/* barlines at a musical spacing, fading out to the right */}
        <g stroke={rule} strokeWidth="1.4">
          {[300, 600, 900].map((x, i) => (
            <line key={x} x1={x} x2={x} y1="92" y2="172" opacity={0.5 - i * 0.13} />
          ))}
        </g>

        <text
          x="64" y="72"
          fill={ink} opacity="0.62"
          fontFamily="ui-serif, Georgia, serif"
          fontSize="19" letterSpacing="5"
        >
          {key.toUpperCase()} · {score.time[0]}/{score.time[1]} · {score.bars} BARS
        </text>

        <text
          x="64" y="228"
          fill={ink}
          fontFamily="ui-serif, Georgia, serif"
          fontSize="54" fontWeight="600"
        >
          {score.title}
        </text>
      </svg>
    </figure>
  );
}
